import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

const RELOAD_STAMP_KEY = 'chunk-reload-at';
const RELOAD_LOOP_WINDOW_MS = 60_000;

// After a deploy, open tabs still reference old hashed chunk URLs that no
// longer exist, so the first lazy() of e.g. STLViewer 404s ("Failed to fetch
// dynamically imported module"). A reload picks up the new index.html.
function reloadOnceOnChunkError(err: unknown): Promise<never> {
    const lastReload = Number(sessionStorage.getItem(RELOAD_STAMP_KEY) ?? 0);
    if (Date.now() - lastReload < RELOAD_LOOP_WINDOW_MS) {
        // Reloaded recently and the chunk is still missing - a real failure,
        // not deploy staleness. Surface it instead of loop-reloading.
        throw err;
    }
    sessionStorage.setItem(RELOAD_STAMP_KEY, String(Date.now()));
    window.location.reload();
    // The page is going away - keep Suspense pending instead of erroring.
    return new Promise<never>(() => {});
}

// Mirrors React.lazy's own signature (its constraint is ComponentType<any>).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(
    factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
    return lazy(() => factory().catch(reloadOnceOnChunkError));
}
